import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const plans = [
  {
    name: 'Connect One',
    price: '$99',
    description: 'For individuals',
    includes: ['1 band included', '1 year membership included'],
    afterYear: '$4.99/mo',
  },
  {
    name: 'Connect Duo',
    price: '$149',
    description: 'For couples',
    includes: ['2 bands included', '1 year membership included'],
    afterYear: '$7.99/mo',
  },
  {
    name: 'Connect Family',
    price: '$249',
    description: 'For families',
    includes: ['4 bands included', '1 year membership included', 'Extra band: $49 each'],
    afterYear: '$14.99/mo',
    popular: true,
  },
  {
    name: 'Connect Care',
    price: '$149',
    description: 'For parents & loved ones',
    includes: ['2 bands included', '1 year membership included'],
    afterYear: '$7.99/mo',
    note: 'For parents 60+ with health monitoring & safety.',
  },
];

const PlansSection = ({ onReserveClick }) => {
  return (
    <section id="plans" className="pt-12 pb-4 md:pt-16 md:pb-5 section-warm" data-testid="plans-section">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 md:mb-10"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-[#1A1A1A] mb-2">
            Choose Your Connect.
          </h2>
          <p className="text-[#5A5A5A] text-base md:text-lg">
            Simple pricing. Bands + 1 year included. No hidden fees.
          </p>
        </motion.div>

        {/* Plans Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-5">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`plan-card relative ${plan.popular 
                ? 'border-2 border-[#1A1A1A] shadow-lg shadow-black/10' 
                : 'border border-black/8'}`}
              data-testid={`plan-card-${plan.name.toLowerCase().replace(' ', '-')}`}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1A1A1A] text-white text-xs 
                                 font-medium px-4 py-1.5 rounded-full whitespace-nowrap shadow-md">
                  Most Popular
                </span>
              )}
              
              {/* Launch Offer Tag */}
              <div className="mb-3">
                <span className="inline-block bg-[#FAF8F5] text-[#6A6A6A] text-xs font-medium px-3 py-1 rounded-full border border-black/5">
                  Launch Offer
                </span>
              </div>
              
              <div className="text-center mb-3">
                <h3 className="text-base font-semibold text-[#1A1A1A] mb-0.5">{plan.name}</h3>
                <p className="text-sm text-[#7A7A7A] mb-2">{plan.description}</p>
                <div className="text-3xl md:text-4xl font-semibold text-[#1A1A1A]">{plan.price}</div>
              </div>

              {/* Includes */}
              <div className="mb-3">
                <p className="text-xs uppercase tracking-wider text-[#8A8A8A] mb-1.5">Includes</p>
                <ul className="space-y-1">
                  {plan.includes.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-[#4A4A4A]">
                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {plan.note && (
                <p className="text-xs text-[#6A6A6A] mb-2 pb-2 border-b border-black/5">
                  {plan.note}
                </p>
              )}

              {/* After 12 months */}
              <div className="mb-3">
                <p className="text-xs uppercase tracking-wider text-[#8A8A8A] mb-0.5">After 12 months</p>
                <p className="text-lg font-semibold text-[#1A1A1A]">{plan.afterYear}</p>
              </div>

              <button
                onClick={onReserveClick}
                className={`w-full py-2.5 rounded-full text-sm font-medium transition-all duration-300
                           ${plan.popular 
                             ? 'bg-[#1A1A1A] text-white hover:bg-[#333] shadow-md' 
                             : 'bg-black/5 text-[#1A1A1A] hover:bg-black/10'
                           }`}
              >
                {plan.popular ? 'Reserve Now →' : 'Reserve Now'}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Simple pricing summary line */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-5"
        >
          <p className="text-[#4A4A4A] text-sm font-medium">
            After 12 months: <span className="text-[#1A1A1A]">One $4.99/mo</span> • <span className="text-[#1A1A1A]">Duo $7.99/mo</span> • <span className="text-[#1A1A1A]">Family $14.99/mo</span> • <span className="text-[#1A1A1A]">Care $7.99/mo</span>
          </p>
        </motion.div>

        {/* Add-ons Strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="addon-strip flex flex-col sm:flex-row items-center justify-between gap-2 max-w-xl mx-auto mb-0"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-black/5 flex items-center justify-center">
              <span className="text-base font-medium">+</span>
            </div>
            <div>
              <p className="font-medium text-[#1A1A1A] text-sm">Extra Band — $49 each</p>
              <p className="text-xs text-[#6A6A6A]">For additional kids or family members</p>
            </div>
          </div>
          <span className="text-xs text-[#8A8A8A] uppercase tracking-wider">Hardware add-on</span>
        </motion.div>
      </div>
    </section>
  );
};

export default PlansSection;
